import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import type { AgentWizardData } from '@/schemas/agent-wizard'
import { LLM_PROVIDERS, MEMORY_TYPES } from '@/schemas/agent-wizard'
import {
  Cpu,
  FileText,
  Zap,
  Brain,
  Settings,
  Check,
  X,
} from 'lucide-react'
import { cn } from '@/lib/utils'

interface AgentPreviewProps {
  className?: string
}

export function AgentPreview({ className }: AgentPreviewProps) {
  const { t } = useTranslation('agents')
  const { watch } = useFormContext<AgentWizardData>()

  const formData = watch()

  const providerLabel = formData.provider
    ? LLM_PROVIDERS[formData.provider as keyof typeof LLM_PROVIDERS]?.label
    : null

  const modelLabel = formData.provider && formData.model
    ? LLM_PROVIDERS[formData.provider as keyof typeof LLM_PROVIDERS]?.models.find(
        (m) => m.value === formData.model
      )?.label
    : null

  const memoryLabel = formData.memory_type
    ? MEMORY_TYPES.find((m) => m.value === formData.memory_type)?.label
    : null

  // Completion calculation
  const completionItems = [
    { key: 'name', done: Boolean(formData.name && formData.name.length >= 2) },
    { key: 'model', done: Boolean(formData.provider && formData.model) },
    { key: 'prompt', done: Boolean(formData.system_prompt) },
    { key: 'tools', done: Boolean(formData.tool_ids?.length) },
    { key: 'knowledge', done: Boolean(formData.knowledge_base_ids?.length) },
  ]
  const doneCount = completionItems.filter((i) => i.done).length
  const total = completionItems.length
  const percent = Math.round((doneCount / total) * 100)

  // Ring SVG params
  const ringSize = 48
  const strokeWidth = 3.5
  const radius = (ringSize - strokeWidth) / 2
  const circumference = 2 * Math.PI * radius
  const offset = circumference - (percent / 100) * circumference

  return (
    <div className={cn('space-y-5', className)}>
      {/* Agent Identity Card */}
      <div className="relative p-5 rounded-2xl bg-gradient-to-br from-indigo-500/[0.08] to-purple-500/[0.06] border border-indigo-500/15 overflow-hidden">
        {/* Subtle shimmer overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/[0.02] to-transparent animate-shimmer pointer-events-none" />

        <div className="relative flex items-start gap-4">
          {/* Avatar with completion ring */}
          <div className="relative flex-shrink-0">
            <svg width={ringSize} height={ringSize} className="completion-ring">
              <circle
                cx={ringSize / 2}
                cy={ringSize / 2}
                r={radius}
                fill="none"
                strokeWidth={strokeWidth}
                className="completion-ring-track"
              />
              <circle
                cx={ringSize / 2}
                cy={ringSize / 2}
                r={radius}
                fill="none"
                strokeWidth={strokeWidth}
                strokeLinecap="round"
                strokeDasharray={circumference}
                strokeDashoffset={offset}
                className="completion-ring-fill"
              />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center text-2xl">
              {formData.icon || '🤖'}
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <h3 className="text-base font-semibold text-white line-clamp-2">
              {formData.name || t('wizard.preview.defaultName')}
            </h3>
            <p className="text-xs text-gray-400 line-clamp-2 mt-1">
              {formData.description || t('wizard.preview.noDescription')}
            </p>
            <div className="flex items-center gap-1.5 mt-2">
              <span
                className={cn(
                  'px-2 py-0.5 rounded-full text-[10px] font-medium',
                  formData.is_active
                    ? 'bg-emerald-500/20 text-emerald-400'
                    : 'bg-gray-700/60 text-gray-400'
                )}
              >
                {formData.is_active ? t('wizard.preview.active') : t('wizard.preview.draft')}
              </span>
              {formData.is_template && (
                <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-indigo-500/20 text-indigo-400">
                  {t('wizard.preview.template')}
                </span>
              )}
              <span className="text-[10px] text-gray-500 ml-auto">
                {percent}%
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Configuration Summary */}
      <div className="space-y-2">
        <SummaryItem
          icon={<Cpu className="h-3.5 w-3.5" />}
          label={t('wizard.preview.model')}
          value={
            providerLabel && modelLabel
              ? `${providerLabel} - ${modelLabel}`
              : t('wizard.preview.notConfigured')
          }
          configured={Boolean(formData.provider && formData.model)}
        />

        {(formData.provider || formData.model) && (
          <div className="pl-7 grid grid-cols-2 gap-1.5 text-[11px]">
            <div>
              <span className="text-gray-500">{t('wizard.preview.temperature')}:</span>{' '}
              <span className="text-gray-300 font-mono">
                {formData.temperature?.toFixed(1) ?? '0.7'}
              </span>
            </div>
            <div>
              <span className="text-gray-500">{t('wizard.preview.maxTokens')}:</span>{' '}
              <span className="text-gray-300 font-mono">
                {formData.max_tokens ?? 2048}
              </span>
            </div>
          </div>
        )}

        <SummaryItem
          icon={<FileText className="h-3.5 w-3.5" />}
          label={t('wizard.preview.systemPrompt')}
          value={
            formData.system_prompt
              ? t('wizard.preview.characters', { count: formData.system_prompt.length })
              : t('wizard.preview.notConfigured')
          }
          configured={Boolean(formData.system_prompt)}
        />

        <SummaryItem
          icon={<Zap className="h-3.5 w-3.5" />}
          label={t('wizard.preview.tools')}
          value={
            formData.tool_ids?.length
              ? t('wizard.preview.toolsSelected', { count: formData.tool_ids.length })
              : t('wizard.preview.noToolsSelected')
          }
          configured={Boolean(formData.tool_ids?.length)}
        />

        <SummaryItem
          icon={<Brain className="h-3.5 w-3.5" />}
          label={t('wizard.preview.knowledgeBase')}
          value={
            formData.knowledge_base_ids?.length
              ? t('wizard.preview.toolsSelected', { count: formData.knowledge_base_ids.length })
              : t('wizard.preview.noToolsSelected')
          }
          configured={Boolean(formData.knowledge_base_ids?.length)}
        />

        <SummaryItem
          icon={<Settings className="h-3.5 w-3.5" />}
          label={t('wizard.preview.memory')}
          value={memoryLabel || t('wizard.preview.default')}
          configured={Boolean(formData.memory_type)}
        />
      </div>

      {/* Checklist */}
      <div className="p-3.5 rounded-xl bg-white/[0.03] border border-gray-800/50">
        <p className="text-xs font-medium text-gray-300 mb-2.5">
          {t('wizard.preview.checklist')}
        </p>
        <div className="space-y-1.5">
          <ChecklistItem
            label={t('wizard.preview.agentName')}
            checked={Boolean(formData.name && formData.name.length >= 2)}
            optionalLabel={t('wizard.preview.optional')}
          />
          <ChecklistItem
            label={t('wizard.preview.languageModel')}
            checked={Boolean(formData.provider && formData.model)}
            optional
            optionalLabel={t('wizard.preview.optional')}
          />
          <ChecklistItem
            label={t('wizard.preview.systemPrompt')}
            checked={Boolean(formData.system_prompt)}
            optional
            optionalLabel={t('wizard.preview.optional')}
          />
          <ChecklistItem
            label={t('wizard.preview.tools')}
            checked={Boolean(formData.tool_ids?.length)}
            optional
            optionalLabel={t('wizard.preview.optional')}
          />
          <ChecklistItem
            label={t('wizard.preview.knowledgeBase')}
            checked={Boolean(formData.knowledge_base_ids?.length)}
            optional
            optionalLabel={t('wizard.preview.optional')}
          />
        </div>
      </div>
    </div>
  )
}

/* ─── Sub-components ─── */

interface SummaryItemProps {
  icon: React.ReactNode
  label: string
  value: string
  configured: boolean
}

function SummaryItem({ icon, label, value, configured }: SummaryItemProps) {
  return (
    <div className="flex items-center gap-2.5 px-3 py-2 rounded-lg bg-white/[0.025] transition-colors hover:bg-white/[0.04]">
      <div className={cn('flex-shrink-0', configured ? 'text-indigo-400' : 'text-gray-600')}>
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[11px] text-gray-500 leading-none mb-0.5">{label}</p>
        <p className={cn('text-xs truncate', configured ? 'text-gray-200' : 'text-gray-500')}>
          {value}
        </p>
      </div>
    </div>
  )
}

interface ChecklistItemProps {
  label: string
  checked: boolean
  optional?: boolean
  optionalLabel: string
}

function ChecklistItem({ label, checked, optional, optionalLabel }: ChecklistItemProps) {
  return (
    <div className="flex items-center gap-2">
      <div
        className={cn(
          'flex h-4 w-4 items-center justify-center rounded-full transition-colors',
          checked ? 'bg-emerald-500/20 text-emerald-400' : 'bg-gray-800 text-gray-600'
        )}
      >
        {checked ? <Check className="h-2.5 w-2.5" /> : <X className="h-2.5 w-2.5" />}
      </div>
      <span className={cn('text-xs', checked ? 'text-gray-300' : 'text-gray-500')}>
        {label}
        {optional && !checked && (
          <span className="text-gray-600 ml-1">{optionalLabel}</span>
        )}
      </span>
    </div>
  )
}
